/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import * as React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useNavigate, useLocation } from 'react-router-dom';
import TranslateIcon from '@mui/icons-material/Translate';
// import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
// import EditRoad from '@mui/icons-material/EditRoad';
// import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import { Collapse, Divider, List } from '@mui/material';
import { ExpandLess, ExpandMore, Tune } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
// import RuleIcon from '@mui/icons-material/Rule';
// import {
//   ASSISTANT_TABS,
//   aiAssistant,
// } from '@ink-ai/portal/reducers/ai-assistant';
// import { inspectActions } from '@ink-ai/portal/reducers/inspect';
// import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';
import { useTranslation } from 'react-i18next';
import { Bolt, BookOpenText, BrainCircuit, Cable, House } from 'lucide-react';
import { styled } from '@mui/system';
import { hasAnyRole, adminRolesForEnv, superAdminRolesForEnv } from '@ink-ai/portal/common/authHelper';

const MnuItemIcon = styled(ListItemIcon)<{ selected?: boolean }>(({ theme, selected }) => ({
  color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  '& .MuiSvgIcon-root': {
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  },
}));

export const MainListItems = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const authState = useSelector((state: RootState) => state.auth);
  const isAdmin = hasAnyRole(authState.group, adminRolesForEnv());
  // Extraction (glossary/reference) and the dashboard are SuperAdmin-only; plain
  // Admin keeps glossary/reference management but no longer sees these.
  const isSuperAdmin = hasAnyRole(authState.group, superAdminRolesForEnv());
  
  // 分割线下方菜单项的收起展开状态
  const [menuOpen, setMenuOpen] = React.useState(true);

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <React.Fragment>
      <ListItemButton sx={{ padding: '8px 12px' }} onClick={() => navigate('/')}
          selected={location.pathname === '' || location.pathname === '/'}
        >
        <MnuItemIcon selected={location.pathname === '' || location.pathname === '/'}>
          <House />
        </MnuItemIcon>
        <ListItemText primary={t('common.home')} />
      </ListItemButton>
      
      {/* Medical Translator Drawer */}
      {/* <ListItemButton sx={{ padding: '8px 12px' }}onClick={handleMedicalTranslatorClick}>
        {appState.leftDrawerOpen ? (
          <ListItemText primary={t('navigation.medicalTranslator')} />
        ) : (
          <MnuItemIcon>
            <TranslateIcon />
          </MnuItemIcon>
        )}
        {appState.leftDrawerOpen &&
          (medicalTranslatorOpen ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
      <Collapse in={medicalTranslatorOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton sx={{ padding: '8px 12px' }}
            onClick={() => navigate('/translate-tasks')} 
            selected={location.pathname === '/translate-tasks' || location.pathname === '/medical-translator'}
          >
            <MnuItemIcon>
              <TranslateIcon />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.medicalTranslator')} />
          </ListItemButton>
          <ListItemButton sx={{ padding: '8px 12px' }}
            onClick={() => navigate('/glossary')} 
            selected={location.pathname === '/glossary' || location.pathname.startsWith('/glossary/')}
          >
            <MnuItemIcon>
              <BookOpenText />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.glossary')} />
          </ListItemButton>
          <ListItemButton sx={{ padding: '8px 12px' }}
            onClick={() => navigate('/reference-store')} 
            selected={location.pathname === '/reference-store' || location.pathname.startsWith('/reference-store/')}
          >
            <MnuItemIcon>
              <LibraryBooksOutlinedIcon />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.referenceStore')} />
          </ListItemButton>
          <ListItemButton sx={{ padding: '8px 12px' }}
            onClick={() => navigate('/extraction/glossary/tasks')} 
            selected={location.pathname === '/extraction/glossary' || location.pathname.startsWith('/extraction/glossary/')}
          >
            <MnuItemIcon>
              <PlagiarismIcon />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.glossaryExtraction')} />
          </ListItemButton>
        </List>
      </Collapse> */}
      
      {/* Medical Writer Drawer */}
      {/* <ListItemButton sx={{ padding: '8px 12px' }}onClick={handleMedicalWriterClick}>
        {appState.leftDrawerOpen ? (
          <ListItemText primary={t('navigation.medicalWriter')} />
        ) : (
          <MnuItemIcon>
            <HistoryEduIcon />
          </MnuItemIcon>
        )}
        {appState.leftDrawerOpen &&
          (medicalWriterOpen ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton> */}

      <ListItemButton sx={{ padding: '8px 12px' }}
        onClick={() => navigate('/text-translator')}
        selected={location.pathname === '/text-translator'}
      >
        <MnuItemIcon selected={location.pathname === '/text-translator'}>
          <TranslateIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.textTranslator')} />
      </ListItemButton>

      <ListItemButton sx={{ padding: '8px 12px' }}
        onClick={() => navigate('/translate-tasks')} 
        selected={location.pathname === '/translate-tasks' || location.pathname === '/document-translator'}
      >
        <MnuItemIcon selected={location.pathname === '/translate-tasks' || location.pathname === '/document-translator'}>
          <DescriptionOutlinedIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.documentsTranslator')} />
      </ListItemButton>
      
      <ListItemButton sx={{ padding: '8px 12px' }}
        onClick={() => navigate('/glossary')} 
        selected={location.pathname === '/glossary' || location.pathname.startsWith('/glossary/')}
      >
        <MnuItemIcon selected={location.pathname === '/glossary' || location.pathname.startsWith('/glossary/')}>
          <BookOpenText />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.glossary')} />
      </ListItemButton>
      
      <ListItemButton sx={{ padding: '8px 12px' }}
        onClick={() => navigate('/reference-store')} 
        selected={location.pathname === '/reference-store' || location.pathname.startsWith('/reference-store/')}
      >
        <MnuItemIcon selected={location.pathname === '/reference-store' || location.pathname.startsWith('/reference-store/')}>
          <LibraryBooksOutlinedIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.referenceStore')} />
      </ListItemButton>
      
      
      <ListItemButton sx={{ padding: '8px 12px' }}
        onClick={() => navigate('/settings')}
        selected={location.pathname === '/settings'}
        // sx={{ pl: 4 }}
      >
        <MnuItemIcon selected={location.pathname === '/settings'}>
          <Bolt />
        </MnuItemIcon>
        <ListItemText primary={t('common.settings')} />
      </ListItemButton>

      <Divider />
      
      {/* 更多功能（仅管理员可见）*/}
      {isAdmin && (
        <>
          <ListItemButton sx={{ padding: '8px 12px' }}onClick={handleMenuClick}>
            <MnuItemIcon>
              <Tune />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.moreFeatures')} />
            {menuOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={menuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton sx={{ padding: '8px 12px' }}
                onClick={() => navigate('/glossary-management')} 
                selected={location.pathname === '/glossary-management' || location.pathname.startsWith('/glossary-management/')}
                // sx={{ pl: 4 }}
              >
                <MnuItemIcon selected={location.pathname === '/glossary-management'}>
                  <ViewWeekOutlinedIcon />
                </MnuItemIcon>
                <ListItemText primary={t('navigation.glossaryManagement')} />
              </ListItemButton>
              
              {isSuperAdmin && (
              <ListItemButton sx={{ padding: '8px 12px' }}
                onClick={() => navigate('/extraction/glossary/tasks')}
                selected={location.pathname === '/extraction/glossary' || location.pathname.startsWith('/extraction/glossary/')}
                // sx={{ pl: 4 }}
              >
                <MnuItemIcon selected={location.pathname === '/extraction/glossary' || location.pathname.startsWith('/extraction/glossary/')}>
                  <PlagiarismIcon />
                </MnuItemIcon>
                <ListItemText primary={t('navigation.glossaryExtraction')} />
              </ListItemButton>
              )}

              <ListItemButton sx={{ padding: '8px 12px' }}
                onClick={() => navigate('/reference-management')}
                selected={location.pathname === '/reference-management' || location.pathname.startsWith('/reference-management/')}
                // sx={{ pl: 4 }}
              >
                <MnuItemIcon selected={location.pathname === '/reference-management'}>
                  <WidgetsOutlinedIcon />
                </MnuItemIcon>
                <ListItemText primary={t('navigation.referenceManagement')} />
              </ListItemButton>
              
              {isSuperAdmin && (
              <ListItemButton sx={{ padding: '8px 12px' }}
                onClick={() => navigate('/extraction/reference/tasks')}
                selected={location.pathname === '/extraction/reference' || location.pathname.startsWith('/extraction/reference/')}
                // sx={{ pl: 4 }}
              >
                <MnuItemIcon selected={location.pathname === '/extraction/reference' || location.pathname.startsWith('/extraction/reference/')}>
                  <FindInPageOutlinedIcon />
                </MnuItemIcon>
                <ListItemText primary={t('navigation.referenceExtraction')} />
              </ListItemButton>
              )}

              {isSuperAdmin && (
              <ListItemButton sx={{ padding: '8px 12px' }}
                onClick={() => navigate('/dashboard')}
                selected={location.pathname === '/dashboard'}
                // sx={{ pl: 4 }}
              >
                <MnuItemIcon selected={location.pathname === '/dashboard'}>
                  <InsertChartOutlinedIcon />
                </MnuItemIcon>
                <ListItemText primary={t('navigation.dashboard')} />
              </ListItemButton>
              )}
            </List>
          </Collapse>
        </>
      )}
    </React.Fragment>
  );
};
